<template>
  <div class="workforce-app">
    <!-- Loading Overlay -->
    <v-overlay :value="globalLoading" opacity="0.85" z-index="9999">
      <div class="text-center loading-overlay-content">
        <v-progress-circular
          indeterminate
          size="72"
          width="6"
          color="#00897B"
        ></v-progress-circular>
        <div class="mt-4 loading-text">{{ loadingMessage }}</div>
      </div>
    </v-overlay>

    <!-- Error Alert -->
    <v-slide-y-transition>
      <v-alert
        v-if="error"
        type="error"
        dismissible
        @input="error = null"
        class="ma-4 error-alert"
        elevation="4"
        prominent
      >
        <template v-slot:prepend>
          <v-icon large>mdi-alert-circle-outline</v-icon>
        </template>
        {{ error }}
      </v-alert>
    </v-slide-y-transition>

    <!-- Bottom Navigation -->
    <v-bottom-navigation
      :color="COLORS.PRIMARY"
      horizontal
      :background-color="COLORS.PRIMARY"
      dark
      class="bottom-nav-bar"
    >
      <v-btn to="/college/personnel_etc" large class="mr-10">
        <span class="bottom-navi">ข้อมูลบุคคลสถานศึกษา</span>
        <v-icon>mdi-account-multiple</v-icon>
      </v-btn>

      <v-btn
        v-if="hasPermission('CollegemanpowerHuman')"
        to="/college/rate_workforce_g"
        large
        class="mr-7"
      >
        <span class="bottom-navi">ข้อมูลอัตราสถานศึกษา</span>
        <v-icon>mdi-numeric-1-box</v-icon>
      </v-btn>

      <v-btn
        v-if="hasPermission('CollegemanpowerStudent') && hasRateWorkData"
        to="/college/rate_workforce"
        class="mr-15"
      >
        <span class="bottom-navi">ข้อมูลนักเรียนนักศึกษา</span>
        <v-icon>mdi-numeric-2-box</v-icon>
      </v-btn>

      <v-btn
        v-if="hasPermission('CollegemanpowerCalStD') && hasRateWorkData"
        to="/college/rate_workforce_pro"
        class="mr-14"
      >
        <span class="bottom-navi">ประมวลผลห้องเรียน</span>
        <v-icon>mdi-numeric-3-box</v-icon>
      </v-btn>

      <v-btn
        v-if="canShowWorkforceCal"
        to="/college/rate_workforce_cal"
        class="mr-15"
      >
        <span class="bottom-navi">ประมวลผลอัตรากำลัง</span>
        <v-icon>mdi-numeric-4-box</v-icon>
      </v-btn>

      <v-btn
        v-if="canShowWorkforceCal"
        to="/college/rate_workforce_report"
        class="mr-7"
      >
        <span class="bottom-navi">รายงานผลอัตรากำลัง</span>
        <v-icon>mdi-numeric-5-box</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <!-- Main Container -->
    <v-container id="upgrade" fluid tag="section">
      <v-row>
        <v-col cols="12">
          <base-material-card color="primary">
            <template v-slot:heading>
              <h2 class="font-weight-light heading-title">
                <v-icon large left>mdi-file-send</v-icon>
                โปรแกรมคำนวณอัตรากำลังในสถานศึกษา
                สังกัดสำนักงานคณะกรรมการการอาชีวศึกษา :
                {{ user.college_id_code_type_manpower }}
              </h2>
            </template>

            <!-- Year Selection and Basic Info -->
            <v-card class="mx-auto pa-5 mb-4 info-card">
              <v-row class="mb-4" justify="center" no-gutters>
                <v-col md="10">
                  <v-card outlined class="selection-card">
                    <v-card-text>
                      <v-row align="center">
                        <v-col cols="12" md="4">
                          <v-select
                            v-model="selectedYear"
                            :items="academicYears"
                            item-value="state"
                            item-text="label"
                            @change="onYearChange"
                            label="ปีการศึกษา พ.ศ."
                            outlined
                            dense
                            :loading="yearLoading"
                            class="year-select"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon color="teal">mdi-calendar</v-icon>
                            </template>
                          </v-select>
                        </v-col>

                        <v-col cols="12" md="4">
                          <div class="text-center college-info">
                            <h3>
                              <v-icon small class="mr-1">mdi-domain</v-icon>
                              รหัสสถานศึกษา: {{ user.user_name }}
                            </h3>
                            <h3>
                              <v-icon small class="mr-1">mdi-school</v-icon>
                              ชื่อสถานศึกษา: {{ user.college_name }}
                            </h3>
                          </div>
                        </v-col>

                        <v-col cols="12" md="4">
                          <div class="text-center college-info">
                            <h3>
                              <v-icon small class="mr-1">mdi-tag</v-icon>
                              ประเภทสถานศึกษา: {{ user.name_ctm }}
                            </h3>
                            <h3>
                              <v-icon small class="mr-1"
                                >mdi-calendar-check</v-icon
                              >
                              ปีการศึกษา:
                              {{ currentRateWork.rate_work_g_year }}
                            </h3>

                            <v-btn
                              v-if="isDataConfirmed"
                              rounded
                              color="info"
                              :href="printReportUrl"
                              target="_blank"
                              class="mt-2 print-btn"
                              elevation="2"
                            >
                              <v-icon left>mdi-printer</v-icon>
                              พิมพ์รายงานข้อมูล
                            </v-btn>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Warning for Unconfirmed Data -->
              <v-alert
                v-if="!isDataConfirmed"
                type="warning"
                class="text-center mb-4 warning-alert"
                prominent
                elevation="2"
              >
                <h2>
                  <v-icon large class="mr-2">mdi-alert-outline</v-icon>
                  ท่านยังไม่ได้ยืนยันบันทึกรายการ ในขั้นตอนที่ 4
                  ทำให้ข้อมูลส่วนนี้จะไม่ปรากฎ
                </h2>
              </v-alert>

              <!-- Main Data Section -->
              <div v-if="isDataConfirmed">
                <!-- ==========================================
                     DASHBOARD STATISTICS CARDS
                     ========================================== -->
                <v-row class="mb-6">
                  <!-- Card: รวมบุคลากรทั้งหมด -->
                  <v-col cols="12" sm="6" md="3">
                    <v-card class="stat-card stat-card--total" elevation="4">
                      <v-card-text class="pa-4">
                        <div class="stat-card__icon-wrap stat-card__icon-wrap--teal">
                          <v-icon large color="white">mdi-account-group</v-icon>
                        </div>
                        <div class="stat-card__content">
                          <div class="stat-card__label">รวมอัตรากำลังทั้งหมด</div>
                          <div class="stat-card__value">
                            {{ currentRateWork.rate_man_power_all || 0 }}
                          </div>
                          <div class="stat-card__sub">คน (มีจริง)</div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <!-- Card: ห้องเรียนรวม -->
                  <v-col cols="12" sm="6" md="3">
                    <v-card class="stat-card stat-card--rooms" elevation="4">
                      <v-card-text class="pa-4">
                        <div class="stat-card__icon-wrap stat-card__icon-wrap--blue">
                          <v-icon large color="white"
                            >mdi-google-classroom</v-icon
                          >
                        </div>
                        <div class="stat-card__content">
                          <div class="stat-card__label">จำนวนห้องเรียน</div>
                          <div class="stat-card__value">
                            {{ currentRateWork.rate_room_cal_all || 0 }}
                          </div>
                          <div class="stat-card__sub">ห้อง</div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <!-- Card: เกณฑ์รวม -->
                  <v-col cols="12" sm="6" md="3">
                    <v-card class="stat-card stat-card--criteria" elevation="4">
                      <v-card-text class="pa-4">
                        <div class="stat-card__icon-wrap stat-card__icon-wrap--amber">
                          <v-icon large color="white">mdi-target</v-icon>
                        </div>
                        <div class="stat-card__content">
                          <div class="stat-card__label">เกณฑ์อัตรากำลังรวม</div>
                          <div class="stat-card__value">
                            {{ currentRateWork.rate_criterion_all || 0 }}
                          </div>
                          <div class="stat-card__sub">ตามเกณฑ์</div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <!-- Card: ขาด/เกิน -->
                  <v-col cols="12" sm="6" md="3">
                    <v-card
                      class="stat-card"
                      :class="shortageOverallClass"
                      elevation="4"
                    >
                      <v-card-text class="pa-4">
                        <div
                          class="stat-card__icon-wrap"
                          :class="shortageOverallIconClass"
                        >
                          <v-icon large color="white">{{
                            shortageOverallIcon
                          }}</v-icon>
                        </div>
                        <div class="stat-card__content">
                          <div class="stat-card__label">สถานะอัตรากำลัง</div>
                          <div
                            class="stat-card__value"
                            :class="shortageOverallValueClass"
                          >
                            {{
                              formatShortage(
                                currentRateWork.rate_criterion_incomplete_all
                              )
                            }}
                          </div>
                          <div class="stat-card__sub">
                            {{ shortageOverallLabel }}
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- ==========================================
                     CHARTS ROW 1: Overview
                     ========================================== -->
                <v-row class="mb-6">
                  <!-- Chart: เปรียบเทียบอัตรากำลัง ผู้บริหาร -->
                  <v-col cols="12" md="6">
                    <v-card class="chart-card" elevation="3">
                      <v-card-title class="chart-card__title">
                        <v-icon left color="teal darken-2"
                          >mdi-chart-bar</v-icon
                        >
                        เปรียบเทียบอัตรากำลังผู้บริหาร
                      </v-card-title>
                      <v-card-text>
                        <apexchart
                          type="bar"
                          height="320"
                          :options="adminComparisonChartOptions"
                          :series="adminComparisonSeries"
                        ></apexchart>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <!-- Chart: สัดส่วนบุคลากรตามประเภท -->
                  <v-col cols="12" md="6">
                    <v-card class="chart-card" elevation="3">
                      <v-card-title class="chart-card__title">
                        <v-icon left color="teal darken-2"
                          >mdi-chart-donut</v-icon
                        >
                        สัดส่วนบุคลากรตามประเภท
                      </v-card-title>
                      <v-card-text>
                        <apexchart
                          type="donut"
                          height="320"
                          :options="staffDistributionChartOptions"
                          :series="staffDistributionSeries"
                        ></apexchart>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- ==========================================
                     CHARTS ROW 2: Teacher Analysis
                     ========================================== -->
                <v-row class="mb-6">
                  <!-- Chart: ครูผู้สอนแยกตามประเภทวิชา (มีจริง vs เกณฑ์) -->
                  <v-col cols="12">
                    <v-card class="chart-card" elevation="3">
                      <v-card-title class="chart-card__title">
                        <v-icon left color="teal darken-2"
                          >mdi-chart-timeline-variant-shimmer</v-icon
                        >
                        ครูผู้สอนแยกตามประเภทวิชา : มีจริง (ข้าราชการ +
                        พนักงานราชการ) เทียบกับเกณฑ์
                      </v-card-title>
                      <v-card-text>
                        <apexchart
                          type="bar"
                          height="400"
                          :options="teacherByCategoryChartOptions"
                          :series="teacherByCategorySeries"
                        ></apexchart>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- ==========================================
                     CHARTS ROW 3: Shortage / Surplus Heatmap & Radar
                     ========================================== -->
                <v-row class="mb-6">
                  <!-- Chart: Radar - ความครบถ้วนของอัตรากำลัง -->
                  <v-col cols="12" md="6">
                    <v-card class="chart-card" elevation="3">
                      <v-card-title class="chart-card__title">
                        <v-icon left color="teal darken-2"
                          >mdi-radar</v-icon
                        >
                        ดัชนีความครบถ้วนอัตรากำลัง (%)
                      </v-card-title>
                      <v-card-text>
                        <apexchart
                          type="radar"
                          height="380"
                          :options="radarChartOptions"
                          :series="radarSeries"
                        ></apexchart>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <!-- Chart: ขาด/เกิน แยกตามประเภทวิชา -->
                  <v-col cols="12" md="6">
                    <v-card class="chart-card" elevation="3">
                      <v-card-title class="chart-card__title">
                        <v-icon left color="teal darken-2"
                          >mdi-swap-vertical-bold</v-icon
                        >
                        สถานะขาด/เกิน แยกตามประเภทวิชา
                      </v-card-title>
                      <v-card-text>
                        <apexchart
                          type="bar"
                          height="380"
                          :options="shortageByTypeChartOptions"
                          :series="shortageBySeries"
                        ></apexchart>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- ==========================================
                     CHARTS ROW 4: Branch-level detail
                     ========================================== -->
                <v-row class="mb-6" v-if="processedDetailedData.length > 0">
                  <!-- Chart: Top 10 สาขาที่ขาดแคลนมากที่สุด -->
                  <v-col cols="12" md="6">
                    <v-card class="chart-card" elevation="3">
                      <v-card-title class="chart-card__title">
                        <v-icon left color="red darken-1"
                          >mdi-arrow-down-bold</v-icon
                        >
                        Top 10 สาขาที่ขาดแคลนอัตรากำลังมากที่สุด
                      </v-card-title>
                      <v-card-text>
                        <apexchart
                          type="bar"
                          height="380"
                          :options="topShortageChartOptions"
                          :series="topShortageSeries"
                        ></apexchart>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <!-- Chart: Top 10 สาขาที่มีนักเรียนมากที่สุด -->
                  <v-col cols="12" md="6">
                    <v-card class="chart-card" elevation="3">
                      <v-card-title class="chart-card__title">
                        <v-icon left color="blue darken-1"
                          >mdi-account-school</v-icon
                        >
                        Top 10 สาขาที่มีนักเรียนมากที่สุด
                      </v-card-title>
                      <v-card-text>
                        <apexchart
                          type="bar"
                          height="380"
                          :options="topStudentChartOptions"
                          :series="topStudentSeries"
                        ></apexchart>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- ==========================================
                     SUMMARY TABLE
                     ========================================== -->
                <v-card outlined elevation="2" class="mb-6 summary-table-card">
                  <v-card-title class="summary-table-card__title">
                    <v-icon left color="teal darken-2">mdi-table</v-icon>
                    สรุปข้อมูลอัตรากำลัง
                  </v-card-title>

                  <v-card-text>
                    <div class="table-responsive">
                      <v-simple-table dense class="summary-table">
                        <thead>
                          <tr>
                            <th class="text-center font-weight-bold">
                              ประเภทอัตรา
                            </th>
                            <th class="text-center font-weight-bold">มีจริง</th>
                            <th
                              class="text-center font-weight-bold th-highlight"
                            >
                              จำนวนห้องเรียน
                            </th>
                            <th class="text-center font-weight-bold">เกณฑ์</th>
                            <th
                              class="text-center font-weight-bold th-highlight"
                            >
                              อัตรากำลังรวม
                            </th>
                            <th class="text-center font-weight-bold">
                              (-ขาด/+เกิน)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <!-- Director Row -->
                          <tr>
                            <td class="font-weight-medium">ผอ.สถานศึกษา</td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_dr || "-" }}
                            </td>
                            <td class="text-center td-highlight" rowspan="2">
                              {{ currentRateWork.rate_room_cal_all || "-" }}
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_criterion_dr || "-" }}
                            </td>
                            <td class="text-center td-highlight" rowspan="2">
                              {{
                                currentRateWork.rate_man_power_teach_all || "-"
                              }}
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              :class="
                                getShortageClass(
                                  currentRateWork.rate_criterion_incomplete_dr
                                )
                              "
                            >
                              {{
                                formatShortage(
                                  currentRateWork.rate_criterion_incomplete_dr
                                )
                              }}
                            </td>
                          </tr>

                          <!-- Deputy Director Row -->
                          <tr>
                            <td class="font-weight-medium">
                              รอง ผอ.สถานศึกษา
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_sedr || "-" }}
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_criterion_sedr || "-" }}
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              :class="
                                getShortageClass(
                                  currentRateWork.rate_criterion_incomplete_sedr
                                )
                              "
                            >
                              {{
                                formatShortage(
                                  currentRateWork.rate_criterion_incomplete_sedr
                                )
                              }}
                            </td>
                          </tr>

                          <!-- Spacer -->
                          <tr>
                            <td colspan="6" class="pa-1"></td>
                          </tr>

                          <!-- Teachers Summary -->
                          <tr class="teachers-summary-row">
                            <td class="text-center font-weight-bold">
                              ครูผู้สอน (รวม)
                            </td>
                            <td class="text-center font-weight-bold">
                              {{
                                currentRateWork.rate_teach_official_all || "-"
                              }}
                            </td>
                            <td class="text-center font-weight-bold">
                              {{ currentRateWork.rate_teach_civil_all || "-" }}
                            </td>
                            <td class="text-center font-weight-bold td-info">
                              {{
                                currentRateWork.rate_criterion_teach_all || "-"
                              }}
                            </td>
                            <td class="text-center font-weight-bold td-info">
                              {{
                                currentRateWork.rate_criterion_teach_alls || "-"
                              }}
                            </td>
                            <td class="text-center font-weight-bold td-primary">
                              {{
                                formatShortage(
                                  currentRateWork.rate_criterion_incomplete_teach_all
                                )
                              }}
                            </td>
                          </tr>

                          <!-- Headers for teacher types -->
                          <tr class="teacher-type-headers-row">
                            <td></td>
                            <td class="text-center font-weight-medium">
                              ข้าราชการ
                            </td>
                            <td class="text-center font-weight-medium">
                              พนักงานราชการ
                            </td>
                            <td class="text-center font-weight-medium">
                              ข้าราชการ
                            </td>
                            <td class="text-center font-weight-medium">
                              พนักงานราชการ
                            </td>
                            <td class="text-center"></td>
                          </tr>

                          <!-- Teacher Categories -->
                          <tr
                            v-for="category in teacherCategories"
                            :key="category.key"
                            class="teacher-row"
                          >
                            <td class="font-weight-medium">
                              {{ category.name }}
                            </td>
                            <td class="text-center">
                              {{ getTeacherData(category.key, "ta") }}
                            </td>
                            <td class="text-center">
                              {{ getTeacherData(category.key, "tb") }}
                            </td>
                            <td class="text-center">
                              {{ getTeacherData(category.key, "ta_official") }}
                            </td>
                            <td class="text-center">
                              {{ getTeacherData(category.key, "tb_civil") }}
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              :class="
                                getShortageClass(
                                  getTeacherData(category.key, 'incomplete')
                                )
                              "
                            >
                              {{
                                formatShortage(
                                  getTeacherData(category.key, "incomplete")
                                )
                              }}
                            </td>
                          </tr>

                          <!-- Support Staff -->
                          <tr class="support-staff-row">
                            <td class="font-weight-bold">
                              บุคลากรสนับสนุนการสอน
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_sup_ta || "-" }}
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_sup_tb || "-" }}
                            </td>
                            <td class="text-center">
                              {{
                                currentRateWork.rate_work_g_sup_ta_official ||
                                  "-"
                              }}
                            </td>
                            <td class="text-center">
                              {{
                                currentRateWork.rate_work_g_sup_tb_civil || "-"
                              }}
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              :class="
                                getShortageClass(
                                  currentRateWork.rate_work_g_sup_incomplete
                                )
                              "
                            >
                              {{
                                formatShortage(
                                  currentRateWork.rate_work_g_sup_incomplete
                                )
                              }}
                            </td>
                          </tr>

                          <!-- Total Summary -->
                          <tr class="total-summary-row">
                            <td class="text-center font-weight-bold">
                              <h3>รวมอัตรา</h3>
                            </td>
                            <td
                              class="text-center font-weight-bold"
                              colspan="2"
                            >
                              <h3>
                                {{ currentRateWork.rate_man_power_all || "-" }}
                              </h3>
                            </td>
                            <td class="text-center font-weight-bold td-info">
                              {{ currentRateWork.rate_criterion_all || "-" }}
                            </td>
                            <td class="text-center font-weight-bold td-info"></td>
                            <td class="text-center font-weight-bold td-info">
                              {{
                                formatShortage(
                                  currentRateWork.rate_criterion_incomplete_all
                                )
                              }}
                            </td>
                          </tr>

                          <!-- Employee Categories Headers -->
                          <tr class="employee-headers-row">
                            <td></td>
                            <td class="text-center font-weight-medium">
                              ลูกจ้างประจำ
                            </td>
                            <td class="text-center font-weight-medium">
                              ลูกจ้างชั่วคราว(ครูจ้างสอน)
                            </td>
                            <td class="text-center font-weight-medium">
                              ลูกจ้างชั่วคราว(สายสนับสนุน,เจ้าหน้าที่)
                            </td>
                            <td class="text-center"></td>
                            <td class="text-center"></td>
                          </tr>

                          <!-- Employee Data -->
                          <tr>
                            <td class="font-weight-bold">
                              ลูกจ้างประจำ/ชั่วคราว
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_pte_ta || "-" }}
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_pte_tc || "-" }}
                            </td>
                            <td class="text-center">
                              {{ currentRateWork.rate_work_g_pte_tb || "-" }}
                            </td>
                            <td class="text-center"></td>
                            <td class="text-center"></td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- ==========================================
                     DETAILED ANALYSIS TABLE
                     ========================================== -->
                <v-card outlined elevation="2" class="detailed-card">
                  <v-card-title class="d-flex justify-space-between align-center detailed-card__title">
                    <div>
                      <v-icon left color="teal darken-2"
                        >mdi-chart-line</v-icon
                      >
                      แสดงอัตรากำลังประกอบการตัดสินใจ ผู้บริหารสถานศึกษา
                    </div>
                    <v-chip color="orange" outlined>
                      <v-icon left small>mdi-test-tube</v-icon>
                      อยู่ระหว่างทดสอบสถิติ
                    </v-chip>
                  </v-card-title>

                  <!-- Desktop View -->
                  <v-card-text v-if="$vuetify.breakpoint.mdAndUp">
                    <v-data-table
                      :headers="detailedTableHeaders"
                      :items="processedDetailedData"
                      :loading="detailLoading"
                      :items-per-page="itemsPerPage"
                      :footer-props="tableFooterProps"
                      :search="searchQuery"
                      class="detailed-analysis-table"
                      dense
                    >
                      <template v-slot:top>
                        <v-row class="ma-2">
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="searchQuery"
                              label="ค้นหาสาขาวิชา"
                              prepend-inner-icon="mdi-magnify"
                              clearable
                              outlined
                              dense
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6" class="text-right">
                            <v-btn
                              v-if="isDataConfirmed"
                              rounded
                              color="info"
                              :href="detailedPrintReportUrl"
                              target="_blank"
                            >
                              <v-icon left>mdi-printer</v-icon>
                              พิมพ์รายงานรายละเอียด
                            </v-btn>
                          </v-col>
                        </v-row>
                      </template>

                      <template v-slot:item.manPower="{ item }">
                        {{ parseFloat(item.manPower).toFixed(2) }}
                      </template>

                      <template v-slot:item.percentage="{ item }">
                        <span class="font-weight-medium">
                          {{ item.percentage }}%
                        </span>
                      </template>

                      <template v-slot:item.manPowerCalculated="{ item }">
                        <v-chip
                          small
                          :color="
                            item.manPowerCalculated > 0 ? 'success' : 'grey'
                          "
                          dark
                        >
                          {{ item.manPowerCalculated }}
                        </v-chip>
                      </template>

                      <template v-slot:item.officialShortage="{ item }">
                        <v-chip
                          small
                          :color="getShortageChipColor(item.officialShortage)"
                          dark
                        >
                          {{ formatShortage(item.officialShortage) }}
                        </v-chip>
                      </template>

                      <template v-slot:item.civilShortage="{ item }">
                        <v-chip
                          small
                          :color="getShortageChipColor(item.civilShortage)"
                          dark
                        >
                          {{ formatShortage(item.civilShortage) }}
                        </v-chip>
                      </template>

                      <template v-slot:item.overallSummary="{ item }">
                        <v-chip
                          small
                          :color="getShortageChipColor(item.overallSummary)"
                          dark
                        >
                          {{ formatShortage(item.overallSummary) }}
                        </v-chip>
                      </template>
                    </v-data-table>
                  </v-card-text>

                  <!-- Mobile View -->
                  <v-card-text v-else>
                    <v-text-field
                      v-model="searchQuery"
                      label="ค้นหาสาขาวิชา"
                      prepend-inner-icon="mdi-magnify"
                      clearable
                      outlined
                      dense
                      class="mb-4"
                    ></v-text-field>

                    <div v-if="detailLoading" class="text-center pa-4">
                      <v-progress-circular
                        indeterminate
                        color="teal"
                      ></v-progress-circular>
                      <div class="mt-2">กำลังโหลดข้อมูล...</div>
                    </div>

                    <v-expansion-panels
                      v-else-if="processedDetailedData.length > 0"
                      accordion
                    >
                      <v-expansion-panel
                        v-for="item in filteredMobileData"
                        :key="item.index"
                      >
                        <v-expansion-panel-header>
                          <div>
                            <div class="font-weight-bold">
                              {{ item.branch_name_th }}
                            </div>
                            <div class="text-caption grey--text">
                              {{ item.type_course }} - {{ item.year_course }}
                            </div>
                          </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content>
                          <v-simple-table dense>
                            <tbody>
                              <tr>
                                <td class="font-weight-medium">
                                  จำนวนนักเรียน:
                                </td>
                                <td>{{ item.sumStudent }}</td>
                              </tr>
                              <tr>
                                <td class="font-weight-medium">ร้อยละ:</td>
                                <td>{{ item.percentage }}%</td>
                              </tr>
                              <tr>
                                <td class="font-weight-medium">
                                  อัตรากำลัง/สาขา:
                                </td>
                                <td>
                                  <v-chip small color="success" dark>
                                    {{ item.manPowerCalculated }}
                                  </v-chip>
                                </td>
                              </tr>
                              <tr>
                                <td class="font-weight-medium">สรุปภาพรวม:</td>
                                <td>
                                  <v-chip
                                    small
                                    :color="
                                      getShortageChipColor(item.overallSummary)
                                    "
                                    dark
                                  >
                                    {{ formatShortage(item.overallSummary) }}
                                  </v-chip>
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>

                    <!-- Mobile Pagination -->
                    <div
                      class="text-center mt-4"
                      v-if="processedDetailedData.length > mobileItemsPerPage"
                    >
                      <v-pagination
                        v-model="mobilePage"
                        :length="
                          Math.ceil(
                            processedDetailedData.length / mobileItemsPerPage
                          )
                        "
                        :total-visible="5"
                        color="teal"
                      ></v-pagination>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </v-card>
          </base-material-card>
        </v-col>
      </v-row>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="confirmationDialog" persistent max-width="600px">
        <v-card class="confirm-dialog-card">
          <v-card-title class="headline">
            <v-icon left color="warning">mdi-alert</v-icon>
            ยืนยันการบันทึกข้อมูล
          </v-card-title>

          <v-card-text>
            <v-alert type="warning" outlined>
              <div class="font-weight-bold">
                การยืนยันบันทึกข้อมูล
                เมื่อกดยืนยันแล้วจะไม่สามารถแก้ไขข้อมูลใดๆ ได้แล้ว
                กรุณาตรวจสอบข้อมูลให้ถูกต้องก่อนการยืนยัน
              </div>
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              large
              @click="confirmationDialog = false"
              :disabled="confirmLoading"
            >
              <v-icon left>mdi-close</v-icon>
              ยกเลิก
            </v-btn>
            <v-btn
              large
              color="success"
              @click="confirmDataSubmission"
              :loading="confirmLoading"
            >
              <v-icon left>mdi-content-save</v-icon>
              ยืนยันการบันทึกข้อมูล
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackbar.show"
        :timeout="snackbar.timeout"
        :color="snackbar.color"
        top
        right
      >
        <v-icon left>{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}

        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            ปิด
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
// Constants
const API_ENDPOINTS = {
  USER: "user.php",
  RATE_WORK: "rate_work_g.php",
  RATE_WORK_CAL: "rate_work_college_cal_idbranch.php",
  RATE_WORK_ROOM_CHECK: "rate_work_college_cal_room.php",
  SYS_CONFIG: "sys_config_collegemanpower.php"
};

const COLORS = {
  PRIMARY: "teal darken-4",
  SUCCESS: "green lighten-1",
  INFO: "cyan lighten-2",
  WARNING: "orange lighten-1",
  ERROR: "red lighten-1"
};

const CHART_PALETTE = [
  "#00897B", "#26A69A", "#4DB6AC", "#80CBC4",
  "#00695C", "#004D40", "#1DE9B6", "#64FFDA",
  "#FF7043", "#FFB74D", "#42A5F5", "#EF5350",
  "#AB47BC", "#7E57C2"
];

const TEACHER_CATEGORIES = [
  { key: "gs", name: "-ประเภทวิชาสามัญ", shortName: "สามัญ" },
  { key: "is", name: "-ประเภทวิชาอุตสาหกรรม", shortName: "อุตสาหกรรม" },
  { key: "cm", name: "-ประเภทวิชาพาณิชยกรรม", shortName: "พาณิชยกรรม" },
  { key: "art", name: "-ประเภทวิชาศิลปกรรม", shortName: "ศิลปกรรม" },
  { key: "em", name: "-ประเภทวิชาคหกรรม", shortName: "คหกรรม" },
  { key: "ag", name: "-ประเภทวิชาเกษตรกรรม", shortName: "เกษตรกรรม" },
  { key: "fish", name: "-ประเภทวิชาประมง", shortName: "ประมง" },
  { key: "tour", name: "-ประเภทวิชาอุตสาหกรรมการท่องเที่ยว", shortName: "ท่องเที่ยว" },
  { key: "textile", name: "-ประเภทวิชาอุตสาหกรรมสิ่งทอ", shortName: "สิ่งทอ" },
  { key: "insy", name: "-ประเภทวิชาเทคโนโลยีสารสนเทศ", shortName: "IT" },
  { key: "entertain", name: "-ประเภทวิชาอุตสาหกรรมบันเทิงและดนตรี", shortName: "บันเทิง" },
  { key: "navy", name: "-ประเภทวิชาพาณิชยนาวี", shortName: "พาณิชยนาวี" },
  { key: "agriInno", name: "-ประเภทวิชาเทคโนโลยีและนวัตกรรมเกษตรอุตสาหกรรม", shortName: "เกษตรนวัตกรรม" },
  { key: "sc", name: "-หลักสูตรระยะสั้น*", shortName: "ระยะสั้น" }
];

export default {
  name: "ImprovedWorkforceCalculation",

  data() {
    return {
      // Constants
      API_ENDPOINTS,
      COLORS,

      // Loading states
      globalLoading: false,
      yearLoading: false,
      detailLoading: false,
      confirmLoading: false,
      loadingMessage: "กำลังโหลดข้อมูล...",

      // Error handling
      error: null,

      // User and authentication
      user: {},

      // Year selection
      selectedYear: {},
      academicYears: [
        { state: "2565", abbr: "2022", label: "2565 (2022)" },
        { state: "2566", abbr: "2023", label: "2566 (2023)" },
        { state: "2567", abbr: "2024", label: "2567 (2024)" },
        { state: "2568", abbr: "2025", label: "2568 (2025)" },
        { state: "2569", abbr: "2026", label: "2569 (2026)" },
        { state: "2570", abbr: "2027", label: "2570 (2027)" }
      ],

      // Data
      currentRateWork: {},
      detailedData: [],
      sysConfig: {},
      rateWorkCheck: {},
      rateWorkRoomCheck: {},

      // Table configuration
      searchQuery: "",
      itemsPerPage: 25,
      tableFooterProps: {
        "items-per-page-options": [10, 25, 50, 100],
        "items-per-page-text": "แถวต่อหน้า:"
      },

      // Mobile pagination
      mobilePage: 1,
      mobileItemsPerPage: 10,

      // Dialogs
      confirmationDialog: false,

      // Notifications
      snackbar: {
        show: false,
        color: "",
        timeout: 5000,
        icon: "",
        text: ""
      },

      // Teacher categories
      teacherCategories: TEACHER_CATEGORIES
    };
  },

  computed: {
    // ==========================================
    // Permission & Status Checks
    // ==========================================
    hasPermission() {
      return permission => {
        return this.sysConfig[`sys_config_${permission}`] === "1";
      };
    },

    hasRateWorkData() {
      return !!this.rateWorkCheck.rate_work_g_college_code;
    },

    canShowWorkforceCal() {
      return this.rateWorkRoomCheck.status !== false && this.hasRateWorkData;
    },

    canShowReport() {
      return this.rateWorkRoomCheck.status !== false;
    },

    isDataConfirmed() {
      return this.currentRateWork.rate_work_g_status === "Confirm";
    },

    // ==========================================
    // URLs
    // ==========================================
    printReportUrl() {
      if (!this.isDataConfirmed) return "";
      return `#/college/print_rate_workforce_cal/${this.currentRateWork.rate_work_g_year}/${this.currentRateWork.college_code}`;
    },

    detailedPrintReportUrl() {
      if (!this.isDataConfirmed) return "";
      return `#/college/print_rate_workforce_cal2/${this.currentRateWork.rate_work_g_year}/${this.currentRateWork.college_code}`;
    },

    // ==========================================
    // Stat Card: Shortage Overall
    // ==========================================
    shortageOverallNum() {
      const val = this.currentRateWork.rate_criterion_incomplete_all;
      if (val === null || val === undefined || val === "-") return 0;
      return parseFloat(val);
    },
    shortageOverallClass() {
      if (this.shortageOverallNum > 0) return "stat-card--surplus";
      if (this.shortageOverallNum < 0) return "stat-card--shortage";
      return "stat-card--balanced";
    },
    shortageOverallIconClass() {
      if (this.shortageOverallNum > 0) return "stat-card__icon-wrap--green";
      if (this.shortageOverallNum < 0) return "stat-card__icon-wrap--red";
      return "stat-card__icon-wrap--grey";
    },
    shortageOverallIcon() {
      if (this.shortageOverallNum > 0) return "mdi-trending-up";
      if (this.shortageOverallNum < 0) return "mdi-trending-down";
      return "mdi-trending-neutral";
    },
    shortageOverallValueClass() {
      if (this.shortageOverallNum > 0) return "green--text text--darken-2";
      if (this.shortageOverallNum < 0) return "red--text text--darken-2";
      return "";
    },
    shortageOverallLabel() {
      if (this.shortageOverallNum > 0) return "เกินอัตรา";
      if (this.shortageOverallNum < 0) return "ขาดอัตรา";
      return "สมดุล";
    },

    // ==========================================
    // CHART DATA: Admin Comparison Bar Chart
    // ==========================================
    adminComparisonSeries() {
      const rw = this.currentRateWork;
      return [
        {
          name: "มีจริง",
          data: [
            parseFloat(rw.rate_work_g_dr || 0),
            parseFloat(rw.rate_work_g_sedr || 0)
          ]
        },
        {
          name: "เกณฑ์",
          data: [
            parseFloat(rw.rate_criterion_dr || 0),
            parseFloat(rw.rate_criterion_sedr || 0)
          ]
        }
      ];
    },
    adminComparisonChartOptions() {
      return {
        chart: {
          type: "bar",
          fontFamily: "Sarabun, sans-serif",
          toolbar: { show: true, tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false } }
        },
        plotOptions: {
          bar: { horizontal: false, columnWidth: "50%", borderRadius: 6 }
        },
        dataLabels: { enabled: true, style: { fontSize: "13px", fontWeight: 600 } },
        xaxis: { categories: ["ผอ.สถานศึกษา", "รอง ผอ.สถานศึกษา"] },
        yaxis: { title: { text: "จำนวน (คน)" }, min: 0 },
        colors: ["#00897B", "#FFB74D"],
        legend: { position: "top" },
        tooltip: { theme: "dark" },
        grid: { borderColor: "#e0e0e0", strokeDashArray: 4 }
      };
    },

    // ==========================================
    // CHART DATA: Staff Distribution Donut
    // ==========================================
    staffDistributionSeries() {
      const rw = this.currentRateWork;
      return [
        parseFloat(rw.rate_work_g_dr || 0) + parseFloat(rw.rate_work_g_sedr || 0),
        parseFloat(rw.rate_teach_official_all || 0),
        parseFloat(rw.rate_teach_civil_all || 0),
        parseFloat(rw.rate_work_g_sup_ta || 0) + parseFloat(rw.rate_work_g_sup_tb || 0),
        parseFloat(rw.rate_work_g_pte_ta || 0),
        parseFloat(rw.rate_work_g_pte_tc || 0) + parseFloat(rw.rate_work_g_pte_tb || 0)
      ];
    },
    staffDistributionChartOptions() {
      return {
        chart: {
          type: "donut",
          fontFamily: "Sarabun, sans-serif"
        },
        labels: [
          "ผู้บริหาร",
          "ครูผู้สอน (ข้าราชการ)",
          "ครูผู้สอน (พนง.ราชการ)",
          "สนับสนุนการสอน",
          "ลูกจ้างประจำ",
          "ลูกจ้างชั่วคราว"
        ],
        colors: ["#00897B", "#26A69A", "#80CBC4", "#FF7043", "#42A5F5", "#AB47BC"],
        legend: { position: "bottom", fontSize: "13px" },
        plotOptions: {
          pie: {
            donut: {
              size: "62%",
              labels: {
                show: true,
                name: { show: true, fontSize: "14px" },
                value: { show: true, fontSize: "18px", fontWeight: 700 },
                total: {
                  show: true,
                  label: "รวมทั้งหมด",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#333"
                }
              }
            }
          }
        },
        dataLabels: { enabled: false },
        tooltip: {
          y: { formatter: val => val + " คน" }
        },
        responsive: [
          {
            breakpoint: 600,
            options: {
              chart: { height: 280 },
              legend: { position: "bottom", fontSize: "11px" }
            }
          }
        ]
      };
    },

    // ==========================================
    // CHART DATA: Teacher by Category (Grouped Bar)
    // ==========================================
    activeTeacherCategories() {
      return TEACHER_CATEGORIES.filter(cat => {
        const ta = parseFloat(this.currentRateWork[`rate_work_g_${cat.key}_ta`] || 0);
        const tb = parseFloat(this.currentRateWork[`rate_work_g_${cat.key}_tb`] || 0);
        const taOff = parseFloat(this.currentRateWork[`rate_work_g_${cat.key}_ta_official`] || 0);
        const tbCiv = parseFloat(this.currentRateWork[`rate_work_g_${cat.key}_tb_civil`] || 0);
        return (ta + tb + taOff + tbCiv) > 0;
      });
    },
    teacherByCategorySeries() {
      const cats = this.activeTeacherCategories;
      return [
        {
          name: "มีจริง (ข้าราชการ)",
          data: cats.map(c => parseFloat(this.currentRateWork[`rate_work_g_${c.key}_ta`] || 0))
        },
        {
          name: "มีจริง (พนง.ราชการ)",
          data: cats.map(c => parseFloat(this.currentRateWork[`rate_work_g_${c.key}_tb`] || 0))
        },
        {
          name: "เกณฑ์ (ข้าราชการ)",
          data: cats.map(c => parseFloat(this.currentRateWork[`rate_work_g_${c.key}_ta_official`] || 0))
        },
        {
          name: "เกณฑ์ (พนง.ราชการ)",
          data: cats.map(c => parseFloat(this.currentRateWork[`rate_work_g_${c.key}_tb_civil`] || 0))
        }
      ];
    },
    teacherByCategoryChartOptions() {
      const cats = this.activeTeacherCategories;
      return {
        chart: {
          type: "bar",
          fontFamily: "Sarabun, sans-serif",
          stacked: false,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: { horizontal: false, columnWidth: "65%", borderRadius: 4 }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: cats.map(c => c.shortName),
          labels: { rotate: -45, rotateAlways: cats.length > 6, style: { fontSize: "12px" } }
        },
        yaxis: { title: { text: "จำนวน (คน)" }, min: 0 },
        colors: ["#00897B", "#80CBC4", "#FF7043", "#FFCC80"],
        legend: { position: "top", fontSize: "12px" },
        tooltip: { shared: true, intersect: false, theme: "dark" },
        grid: { borderColor: "#e0e0e0", strokeDashArray: 4 }
      };
    },

    // ==========================================
    // CHART DATA: Radar - Completeness Index
    // ==========================================
    radarSeries() {
      const cats = this.activeTeacherCategories;
      const percentages = cats.map(c => {
        const actual = parseFloat(this.currentRateWork[`rate_work_g_${c.key}_ta`] || 0)
          + parseFloat(this.currentRateWork[`rate_work_g_${c.key}_tb`] || 0);
        const criterion = parseFloat(this.currentRateWork[`rate_work_g_${c.key}_ta_official`] || 0)
          + parseFloat(this.currentRateWork[`rate_work_g_${c.key}_tb_civil`] || 0);
        if (criterion === 0) return 0;
        return Math.min(Math.round((actual / criterion) * 100), 150);
      });
      return [{ name: "ความครบถ้วน (%)", data: percentages }];
    },
    radarChartOptions() {
      const cats = this.activeTeacherCategories;
      return {
        chart: {
          type: "radar",
          fontFamily: "Sarabun, sans-serif",
          toolbar: { show: false },
          dropShadow: { enabled: true, blur: 3, left: 1, top: 1 }
        },
        xaxis: {
          categories: cats.map(c => c.shortName)
        },
        yaxis: { show: true, min: 0, max: 150, tickAmount: 3 },
        colors: ["#00897B"],
        fill: { opacity: 0.25 },
        stroke: { width: 2 },
        markers: { size: 4, hover: { size: 6 } },
        plotOptions: {
          radar: { polygons: { strokeColors: "#e0e0e0", connectorColors: "#e0e0e0" } }
        },
        tooltip: {
          y: { formatter: val => val + "%" }
        },
        dataLabels: { enabled: true, style: { fontSize: "11px" }, formatter: val => val + "%" }
      };
    },

    // ==========================================
    // CHART DATA: Shortage by Category (Diverging Bar)
    // ==========================================
    shortageBySeries() {
      const cats = this.activeTeacherCategories;
      return [
        {
          name: "ขาด/เกิน",
          data: cats.map(c => {
            const val = this.currentRateWork[`rate_work_g_${c.key}_incomplete`];
            return parseFloat(val || 0);
          })
        }
      ];
    },
    shortageByTypeChartOptions() {
      const cats = this.activeTeacherCategories;
      return {
        chart: {
          type: "bar",
          fontFamily: "Sarabun, sans-serif",
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            colors: {
              ranges: [
                { from: -1000, to: -0.01, color: "#EF5350" },
                { from: 0, to: 0, color: "#9E9E9E" },
                { from: 0.01, to: 1000, color: "#66BB6A" }
              ]
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: val => {
            if (val > 0) return "+" + val;
            return val;
          },
          style: { fontSize: "12px", fontWeight: 600 }
        },
        xaxis: {
          title: { text: "จำนวน (คน)" },
          labels: { formatter: val => Math.round(val) }
        },
        yaxis: {
          categories: cats.map(c => c.shortName)
        },
        grid: { borderColor: "#e0e0e0", xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
        tooltip: {
          theme: "dark",
          y: {
            formatter: val => {
              if (val > 0) return "เกิน " + val + " คน";
              if (val < 0) return "ขาด " + Math.abs(val) + " คน";
              return "สมดุล";
            }
          }
        },
        annotations: {
          xaxis: [
            {
              x: 0,
              borderColor: "#333",
              strokeDashArray: 0,
              label: {
                text: "เส้นสมดุล",
                style: { fontSize: "11px", color: "#333", background: "#fff" }
              }
            }
          ]
        }
      };
    },

    // ==========================================
    // CHART DATA: Top 10 Shortage Branches
    // ==========================================
    topShortageBranches() {
      if (!this.processedDetailedData.length) return [];
      return [...this.processedDetailedData]
        .filter(d => d.overallSummary < 0)
        .sort((a, b) => a.overallSummary - b.overallSummary)
        .slice(0, 10);
    },
    topShortageSeries() {
      return [
        {
          name: "ขาดอัตรากำลัง",
          data: this.topShortageBranches.map(d => d.overallSummary)
        }
      ];
    },
    topShortageChartOptions() {
      return {
        chart: {
          type: "bar",
          fontFamily: "Sarabun, sans-serif",
          toolbar: { show: true }
        },
        plotOptions: {
          bar: { horizontal: true, borderRadius: 4, barHeight: "65%" }
        },
        colors: ["#EF5350"],
        dataLabels: { enabled: true, style: { fontSize: "12px" } },
        xaxis: {
          title: { text: "จำนวน (คน)" },
          labels: { formatter: val => Math.round(val) }
        },
        yaxis: {
          categories: this.topShortageBranches.map(d => this.truncateText(d.branch_name_th, 25))
        },
        tooltip: { theme: "dark", y: { formatter: val => "ขาด " + Math.abs(val) + " คน" } },
        grid: { borderColor: "#e0e0e0" }
      };
    },

    // ==========================================
    // CHART DATA: Top 10 Student Branches
    // ==========================================
    topStudentBranches() {
      if (!this.processedDetailedData.length) return [];
      return [...this.processedDetailedData]
        .sort((a, b) => parseFloat(b.sumStudent || 0) - parseFloat(a.sumStudent || 0))
        .slice(0, 10);
    },
    topStudentSeries() {
      return [
        {
          name: "จำนวนนักเรียน",
          data: this.topStudentBranches.map(d => parseFloat(d.sumStudent || 0))
        }
      ];
    },
    topStudentChartOptions() {
      return {
        chart: {
          type: "bar",
          fontFamily: "Sarabun, sans-serif",
          toolbar: { show: true }
        },
        plotOptions: {
          bar: { horizontal: true, borderRadius: 4, barHeight: "65%" }
        },
        colors: ["#42A5F5"],
        dataLabels: { enabled: true, style: { fontSize: "12px" } },
        xaxis: {
          title: { text: "จำนวน (คน)" }
        },
        yaxis: {
          categories: this.topStudentBranches.map(d => this.truncateText(d.branch_name_th, 25))
        },
        tooltip: { theme: "dark", y: { formatter: val => val + " คน" } },
        grid: { borderColor: "#e0e0e0" }
      };
    },

    // ==========================================
    // TABLE HEADERS
    // ==========================================
    detailedTableHeaders() {
      return [
        { text: "ลำดับ", value: "index", align: "center", sortable: false, width: "60px" },
        { text: "ประเภท", value: "type_course", align: "start", width: "100px" },
        { text: "หลักสูตร", value: "year_course", align: "center", width: "80px" },
        { text: "สาขาวิชา", value: "branch_name_th", align: "start" },
        { text: "จำนวนนักเรียน", value: "sumStudent", align: "center", width: "120px" },
        { text: "รวม/จำนวนนักเรียนประเภท", value: "sumAllStd", align: "center", width: "150px" },
        { text: "ร้อยละ", value: "percentage", align: "center", width: "80px" },
        { text: "อัตรากำลังคำนวณ", value: "manPower", align: "center", width: "120px" },
        { text: "อัตรากำลัง/สาขา", value: "manPowerCalculated", align: "center", width: "120px" },
        { text: "ข้าราชการ", value: "officialRate", align: "center", width: "100px" },
        { text: "ข้าราชการ(มีอยู่)", value: "countTeach", align: "center", width: "120px" },
        { text: "ข้าราชการ(ขาด)", value: "officialShortage", align: "center", width: "100px" },
        { text: "พนง.", value: "civilRate", align: "center", width: "80px" },
        { text: "พนง.(มีอยู่)", value: "countTeachGov", align: "center", width: "100px" },
        { text: "พนง.(ขาด)", value: "civilShortage", align: "center", width: "100px" },
        { text: "ครูจ้างสอน", value: "countTeachTem", align: "center", width: "100px" },
        { text: "สรุปภาพรวม", value: "overallSummary", align: "center", width: "120px" }
      ];
    },

    // ==========================================
    // PROCESSED DATA
    // ==========================================
    processedDetailedData() {
      if (!Array.isArray(this.detailedData) || this.detailedData.length === 0) {
        return [];
      }

      return this.detailedData.map((item, index) => {
        const percentage = this.calculatePercentage(item.sumStudent, item.sumAllStd);
        const manPowerCalculated = this.calculateManPowerPerBranch(item, percentage);
        const officialRate = this.calculateOfficialRate(manPowerCalculated);
        const civilRate = this.calculateCivilRate(manPowerCalculated);
        const officialShortage = this.calculateOfficialShortage(item, officialRate);
        const civilShortage = this.calculateCivilShortage(item, civilRate);
        const overallSummary = this.calculateOverallSummary(item, manPowerCalculated);

        return {
          ...item,
          index: index + 1,
          percentage,
          manPowerCalculated,
          officialRate,
          civilRate,
          officialShortage,
          civilShortage,
          overallSummary
        };
      });
    },

    filteredMobileData() {
      let filtered = this.processedDetailedData;
      if (!Array.isArray(filtered) || filtered.length === 0) return [];

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          item =>
            (item.branch_name_th && item.branch_name_th.toLowerCase().includes(query)) ||
            (item.type_course && item.type_course.toLowerCase().includes(query))
        );
      }

      const start = (this.mobilePage - 1) * this.mobileItemsPerPage;
      const end = start + this.mobileItemsPerPage;
      return filtered.slice(start, end);
    }
  },

  async mounted() {
    await this.initializeComponent();
  },

  methods: {
    // ==========================================
    // Initialization
    // ==========================================
    async initializeComponent() {
      this.globalLoading = true;
      this.loadingMessage = "กำลังโหลดข้อมูลเริ่มต้น...";

      try {
        await this.loadUserData();
        await Promise.allSettled([this.loadSystemConfig()]);
      } catch (error) {
        this.handleError("ไม่สามารถโหลดข้อมูลได้", error);
      } finally {
        this.globalLoading = false;
      }
    },

    // ==========================================
    // Data Loading
    // ==========================================
    async loadUserData() {
      try {
        const userSession = JSON.parse(localStorage.getItem("user")) || {};
        const result = await this.$http.post(API_ENDPOINTS.USER, {
          ApiKey: "HRvec2021",
          user_ID: userSession.user_ID
        });
        this.user = result.data;
      } catch (error) {
        throw new Error("ไม่สามารถโหลดข้อมูลผู้ใช้ได้");
      }
    },

    async loadRateWorkData() {
      try {
        this.currentRateWork = {};
        let result = await this.$http.post(API_ENDPOINTS.RATE_WORK, {
          ApiKey: "HRvec2021",
          rate_work_g_college_code: this.user.user_code,
          rate_work_g_year: this.selectedYear
        });
        this.currentRateWork = result.data;
      } catch (error) {
        console.error("Error loading rate work data:", error);
      }
    },

    async loadDetailedData() {
      this.detailLoading = true;
      try {
        const result = await this.$http.post(API_ENDPOINTS.RATE_WORK_CAL, {
          ApiKey: "HRvec2021",
          rate_work_college_code: this.user.user_code,
          rate_work_college_year: this.selectedYear
        });

        if (result && result.data) {
          this.detailedData = Array.isArray(result.data) ? result.data : [];
        } else {
          this.detailedData = [];
        }
      } catch (error) {
        console.error("Error loading detailed data:", error);
        this.detailedData = [];
        this.handleError("ไม่สามารถโหลดข้อมูลรายละเอียดได้", error);
      } finally {
        this.detailLoading = false;
      }
    },

    async loadSystemConfig() {
      try {
        const result = await this.$http.post(API_ENDPOINTS.SYS_CONFIG, {
          ApiKey: "HRvec2021",
          sys_config_CollegemanpowerID: "1"
        });
        this.sysConfig = result.data;
      } catch (error) {
        console.error("Error loading system config:", error);
      }
    },

    async loadRateWorkChecks() {
      try {
        const [rateWorkResult, roomCheckResult] = await Promise.allSettled([
          this.$http.post(API_ENDPOINTS.RATE_WORK, {
            ApiKey: "HRvec2021",
            rate_work_g_college_code: this.user.user_code,
            rate_work_g_year: this.selectedYear
          }),
          this.$http.post(API_ENDPOINTS.RATE_WORK_ROOM_CHECK, {
            ApiKey: "HRvec2021",
            rate_work_ccr_code: this.user.user_code,
            rate_work_ccr_year: this.selectedYear,
            rate_work_ccr_check: "OK"
          })
        ]);

        if (rateWorkResult.status) {
          this.rateWorkCheck = rateWorkResult.value.data;
        }
        if (roomCheckResult.status) {
          this.rateWorkRoomCheck = roomCheckResult.value.data;
        }
      } catch (error) {
        console.error("Error loading rate work checks:", error);
      }
    },

    // ==========================================
    // Event Handlers
    // ==========================================
    async onYearChange() {
      this.yearLoading = true;
      this.loadingMessage = "กำลังโหลดข้อมูลปีการศึกษา...";

      try {
        await Promise.allSettled([
          this.loadRateWorkData(),
          this.loadDetailedData(),
          this.loadRateWorkChecks()
        ]);
        this.showSuccessMessage("โหลดข้อมูลปีการศึกษาเรียบร้อยแล้ว");
      } catch (error) {
        this.handleError("ไม่สามารถโหลดข้อมูลปีการศึกษาได้", error);
      } finally {
        this.yearLoading = false;
      }
    },

    async confirmDataSubmission() {
      this.confirmLoading = true;
      try {
        await new Promise(resolve => setTimeout(resolve, 2000));
        this.showSuccessMessage("ยืนยันข้อมูลเรียบร้อยแล้ว");
        this.confirmationDialog = false;
        await this.loadRateWorkData();
      } catch (error) {
        this.handleError("ไม่สามารถยืนยันข้อมูลได้", error);
      } finally {
        this.confirmLoading = false;
      }
    },

    // ==========================================
    // Calculation Methods
    // ==========================================
    calculatePercentage(sumStudent, sumAllStd) {
      if (!sumAllStd || sumAllStd === 0 || !sumStudent) return "0.00";
      return parseFloat(
        (parseFloat(sumStudent) * 100) / parseFloat(sumAllStd)
      ).toFixed(2);
    },

    calculateManPowerPerBranch(item, percentage = null) {
      if (!item || !item.manPower) return "0.00";
      if (percentage === null) {
        percentage = this.calculatePercentage(item.sumStudent, item.sumAllStd);
      }
      return parseFloat(
        (parseFloat(percentage) * parseFloat(item.manPower)) / 100
      ).toFixed(2);
    },

    calculateOfficialRate(manPowerCalculated) {
      if (!manPowerCalculated) return "0.00";
      return parseFloat(parseFloat(manPowerCalculated) * 0.9).toFixed(2);
    },

    calculateCivilRate(manPowerCalculated) {
      if (!manPowerCalculated) return "0.00";
      return parseFloat(parseFloat(manPowerCalculated) * 0.1).toFixed(2);
    },

    calculateOfficialShortage(item, officialRate) {
      if (!item || !officialRate) return 0;
      return Math.round(parseFloat(item.countTeach || 0) - parseFloat(officialRate));
    },

    calculateCivilShortage(item, civilRate) {
      if (!item || !civilRate) return 0;
      return Math.round(parseFloat(item.countTeachGov || 0) - parseFloat(civilRate));
    },

    calculateOverallSummary(item, manPowerCalculated) {
      if (!item || !manPowerCalculated) return 0;
      const totalCurrent =
        parseFloat(item.countTeachTem || 0) +
        parseFloat(item.countTeach || 0) +
        parseFloat(item.countTeachGov || 0);
      return Math.round(totalCurrent - parseFloat(manPowerCalculated));
    },

    // ==========================================
    // Helper Methods
    // ==========================================
    getTeacherData(categoryKey, field) {
      const key = `rate_work_g_${categoryKey}_${field}`;
      return this.currentRateWork[key] || "-";
    },

    formatShortage(value) {
      if (value === null || value === undefined || value === "-") return "-";
      const num = parseFloat(value);
      if (num > 0) return `+${num}`;
      return num.toString();
    },

    getShortageClass(value) {
      if (value === null || value === undefined || value === "-") return "";
      const num = parseFloat(value);
      if (num > 0) return "green--text";
      if (num < 0) return "red--text";
      return "";
    },

    getShortageChipColor(value) {
      if (value === null || value === undefined || value === "-") return "grey";
      const num = parseFloat(value);
      if (num > 0) return "success";
      if (num < 0) return "error";
      return "warning";
    },

    truncateText(text, maxLen) {
      if (!text) return "";
      if (text.length <= maxLen) return text;
      return text.substring(0, maxLen) + "...";
    },

    // ==========================================
    // Notification Methods
    // ==========================================
    showSuccessMessage(message) {
      this.snackbar = { show: true, color: "success", text: message, icon: "mdi-check-circle", timeout: 5000 };
    },

    showErrorMessage(message) {
      this.snackbar = { show: true, color: "error", text: message, icon: "mdi-alert-circle", timeout: 8000 };
    },

    showWarningMessage(message) {
      this.snackbar = { show: true, color: "warning", text: message, icon: "mdi-alert", timeout: 6000 };
    },

    handleError(message, error = null) {
      console.error("Application Error:", error);
      this.error = message;
      this.showErrorMessage(message);
    }
  },

  beforeDestroy() {
    // Cleanup
  }
};
</script>

<style scoped>
/* ==========================================
   BASE
   ========================================== */
.workforce-app {
  font-family: "Sarabun", sans-serif;
}

.bottom-navi {
  font-size: 12pt;
}

.heading-title {
  letter-spacing: 0.3px;
}

/* ==========================================
   LOADING
   ========================================== */
.loading-overlay-content {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 32px 48px;
  backdrop-filter: blur(4px);
}

.loading-text {
  font-size: 16px;
  color: #fff;
  letter-spacing: 0.5px;
}

/* ==========================================
   STAT CARDS
   ========================================== */
.stat-card {
  border-radius: 14px !important;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15) !important;
}

.stat-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-card__icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-card__icon-wrap--teal {
  background: linear-gradient(135deg, #00897b 0%, #26a69a 100%);
}

.stat-card__icon-wrap--blue {
  background: linear-gradient(135deg, #1565c0 0%, #42a5f5 100%);
}

.stat-card__icon-wrap--amber {
  background: linear-gradient(135deg, #f57c00 0%, #ffb74d 100%);
}

.stat-card__icon-wrap--green {
  background: linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%);
}

.stat-card__icon-wrap--red {
  background: linear-gradient(135deg, #c62828 0%, #ef5350 100%);
}

.stat-card__icon-wrap--grey {
  background: linear-gradient(135deg, #616161 0%, #9e9e9e 100%);
}

.stat-card__content {
  flex: 1;
}

.stat-card__label {
  font-size: 13px;
  color: #757575;
  font-weight: 500;
  margin-bottom: 2px;
}

.stat-card__value {
  font-size: 28px;
  font-weight: 700;
  color: #212121;
  line-height: 1.2;
}

.stat-card__sub {
  font-size: 12px;
  color: #9e9e9e;
  margin-top: 2px;
}

.stat-card--shortage {
  border-left: 4px solid #ef5350;
}

.stat-card--surplus {
  border-left: 4px solid #66bb6a;
}

.stat-card--balanced {
  border-left: 4px solid #9e9e9e;
}

/* ==========================================
   CHART CARDS
   ========================================== */
.chart-card {
  border-radius: 14px !important;
  overflow: hidden;
  transition: box-shadow 0.25s ease;
}

.chart-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.chart-card__title {
  font-size: 16px;
  font-weight: 600;
  color: #37474f;
  border-bottom: 1px solid #eceff1;
  padding-bottom: 12px;
}

/* ==========================================
   SUMMARY TABLE
   ========================================== */
.summary-table-card {
  border-radius: 14px !important;
}

.summary-table-card__title {
  font-size: 16px;
  font-weight: 600;
  color: #37474f;
  border-bottom: 1px solid #eceff1;
}

.table-responsive {
  overflow-x: auto;
}

.summary-table th {
  background-color: #e0f2f1 !important;
  color: #004d40 !important;
  font-weight: 700 !important;
  padding: 10px 12px !important;
  font-size: 13px;
}

.summary-table td {
  padding: 8px 12px !important;
  font-size: 13px;
}

.th-highlight {
  background-color: #b2dfdb !important;
}

.td-highlight {
  background-color: #e0f2f1;
}

.td-info {
  background-color: #e0f7fa;
}

.td-primary {
  background-color: #e0f2f1;
}

.teachers-summary-row {
  background-color: rgba(0, 137, 123, 0.08);
}

.teachers-summary-row td {
  border-top: 2px solid #00897b !important;
  border-bottom: 2px solid #00897b !important;
}

.teacher-type-headers-row {
  background-color: #fafafa;
}

.teacher-type-headers-row td {
  font-style: italic;
  color: #616161;
}

.teacher-row:hover {
  background-color: #f5f5f5;
}

.support-staff-row {
  background-color: rgba(255, 112, 67, 0.06);
}

.total-summary-row {
  background-color: rgba(0, 137, 123, 0.12);
}

.total-summary-row td {
  border-top: 3px solid #00897b !important;
}

.employee-headers-row {
  background-color: #fafafa;
}

.employee-headers-row td {
  font-style: italic;
  color: #616161;
}

/* ==========================================
   DETAILED ANALYSIS TABLE
   ========================================== */
.detailed-card {
  border-radius: 14px !important;
}

.detailed-card__title {
  font-size: 16px;
  font-weight: 600;
  color: #37474f;
  border-bottom: 1px solid #eceff1;
}

.detailed-analysis-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

.detailed-analysis-table >>> thead th {
  background-color: #e0f2f1 !important;
  color: #004d40 !important;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
}

/* ==========================================
   MISC
   ========================================== */
.info-card {
  border-radius: 14px !important;
}

.selection-card {
  border-radius: 12px !important;
  border-color: #b2dfdb !important;
}

.college-info h3 {
  font-size: 14px;
  color: #37474f;
  margin-bottom: 4px;
}

.print-btn {
  text-transform: none;
  font-weight: 600;
}

.year-select {
  max-width: 280px;
}

.warning-alert {
  border-radius: 12px !important;
}

.error-alert {
  border-radius: 12px !important;
}

.confirm-dialog-card {
  border-radius: 16px !important;
}

/* ==========================================
   RESPONSIVE
   ========================================== */
@media (max-width: 768px) {
  .bottom-navi {
    font-size: 10pt;
  }

  .stat-card__value {
    font-size: 22px;
  }

  .stat-card__icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 10px;
  }

  .chart-card__title {
    font-size: 14px;
  }
}

@media (max-width: 600px) {
  .v-container {
    padding: 8px;
  }

  .stat-card {
    margin-bottom: 8px;
  }
}

/* ==========================================
   PRINT
   ========================================== */
@media print {
  .v-bottom-navigation,
  .v-btn,
  .v-snackbar,
  .v-overlay,
  .bottom-nav-bar {
    display: none !important;
  }

  .v-card {
    box-shadow: none !important;
    border: 1px solid #ddd;
  }

  .stat-card {
    break-inside: avoid;
  }

  .chart-card {
    break-inside: avoid;
  }
}

/* ==========================================
   ACCESSIBILITY
   ========================================== */
.v-btn:focus {
  outline: 2px solid #00897b;
  outline-offset: 2px;
}

/* ==========================================
   ANIMATIONS
   ========================================== */
.v-card {
  transition: all 0.3s ease;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>